import React, { useCallback, useEffect, useRef, useState } from "react";
import AddToCourseModal from "./AddToCourseModal";
import ConfirmationModal from "../../../../../shared/ConfirmationModal";
import PaginatedTable from "../../../../../shared/PaginatedTable";
import StudentCoursesTableHeader from "./StudentCoursesTableHeader";
import StudentCoursesTableRow from "./StudentCoursesTableRow";

import EduApi from "../../../../../../services/resources/EduApi";
import { ApiCallErrorMessageHandler } from "../../../../../../lib/coc-common-scripts";
import axios from "axios";
import { formatFullName, formatNumber } from "../../../../../../lib";

export default function StudentCoursesTable({ eduStudent, viewOnly }) {
  const [courseToCancel, setCourseToCancel] = useState(null);
  const [
    cancelCourseEnrollmentErrorMessage,
    setCancelCourseEnrollmentErrorMessage,
  ] = useState("");
  const [cancelCourseEnrollmentLoading, setCancelCourseEnrollmentLoading] =
    useState(false);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [courseToMarkReview, setCourseToMarkReview] = useState(null);
  const [markReviewErrorMessage, setMarkReviewErrorMessage] = useState("");
  const [markReviewLoading, setMarkReviewLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [results, setResults] = useState(12);
  const [showAddToCourseModal, setShowAddToCourseModal] = useState(false);
  const [showCancelCourseEnrollmentModal, setShowCancelCourseEnrollmentModal] =
    useState(false);
  const [showMarkReviewModal, setShowMarkReviewModal] = useState(false);
  const [sortBy] = useState("date");
  const [success, setSuccess] = useState(true);
  const [totalCourses, setTotalCourses] = useState(0);

  const apiSignal = useRef(axios.CancelToken.source());

  useEffect(() => {
    const signal = apiSignal.current;
    return () => signal.cancel();
  }, []);

  const getCourses = useCallback(
    (pg = 1, res = results, sort = sortBy) => {
      setLoading(true);
      setPage(pg);
      setResults(res);
      setSuccess(true);

      EduApi.getEduStudentCourses(
        apiSignal.current.token,
        eduStudent.eduStudentEnrollmentID,
        pg,
        res,
        sort,
      )
        .then(({ courses, totalCourses }) => {
          setCourses(courses);
          setLoading(false);
          setTotalCourses(totalCourses);
        })
        .catch((err) => {
          if (!axios.isCancel(err)) {
            setCourses([]);
            setLoading(false);
            setSuccess(false);
            setTotalCourses(0);
          }
        });
    },
    [eduStudent.eduStudentEnrollmentID, results, sortBy],
  );

  const getCoursesProgressWidth = (numCompleted, numEnrolled) => {
    const width = (numCompleted / numEnrolled) * 100;
    return width + "%";
  };

  const markReviewCompleted = useCallback(() => {
    setMarkReviewErrorMessage("");
    setMarkReviewLoading(true);

    EduApi.markEduStudentCourseReviewCompleted(apiSignal.current.token, {
      id: courseToMarkReview.eduStudentCourseScheduleEnrollmentID,
      eduCourseScheduleID: courseToMarkReview.courseScheduleID,
      eduStudentEnrollmentID: eduStudent.eduStudentEnrollmentID,
      didCompleteSurvey: true,
    })
      .then(() => {
        setShowMarkReviewModal(false);
        setMarkReviewLoading(false);
        setCourseToMarkReview(null);
        getCourses(page);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setMarkReviewErrorMessage(ApiCallErrorMessageHandler(err));
          setMarkReviewLoading(false);
        }
      });
  }, [eduStudent.eduStudentEnrollmentID, getCourses, courseToMarkReview, page]);

  const handleCancelCourseEnrollment = useCallback(() => {
    setCancelCourseEnrollmentErrorMessage("");
    setCancelCourseEnrollmentLoading(true);

    EduApi.removeEduStudentFromCourse(
      apiSignal.current.token,
      courseToCancel.eduStudentCourseScheduleEnrollmentID,
    )
      .then(() => {
        setShowCancelCourseEnrollmentModal(false);
        setCancelCourseEnrollmentLoading(false);
        setCourseToCancel(null);
        getCourses();
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setCancelCourseEnrollmentErrorMessage(
            ApiCallErrorMessageHandler(err),
          );
          setCancelCourseEnrollmentLoading(false);
        }
      });
  }, [courseToCancel, getCourses]);

  const { numOfCoursesCompleted, student, status } = eduStudent;
  const { firstName, lastName } = student.person;

  return (
    <div>
      <div className="flex flex-justify-space mb-24">
        <p className="fw-500 xxl-text mb-24">Course History</p>
        <div className="flex flex-align-center">
          <div className="student-profile-courses-progress">
            <div className="flex flex-justify-space flex-align-center">
              <p className="fw-700">Courses Completed</p>
              <p>{formatNumber(numOfCoursesCompleted)}/8</p>
            </div>
            <div
              className="student-courses-progress-bar relative full-width mt-8"
              style={{ height: "12px" }}
            >
              <div className="progress-bar-outer absolute full-width" />
              <div
                className="progress-bar-inner absolute"
                style={{
                  width: getCoursesProgressWidth(
                    numOfCoursesCompleted > 8 ? 8 : numOfCoursesCompleted,
                    8,
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {!viewOnly && (
        <div className="flex flex-justify-end">
          <p
            className="accent-text-secondary fw-700 uppercase-text link-text"
            onClick={() => setShowAddToCourseModal(!showAddToCourseModal)}
          >
            + Add to course
          </p>
          {showAddToCourseModal && (
            <AddToCourseModal
              close={() => setShowAddToCourseModal(false)}
              getCourses={getCourses}
              name={`${firstName} ${lastName}`}
              studentID={eduStudent.eduStudentEnrollmentID}
              show={true}
            />
          )}
        </div>
      )}
      <PaginatedTable
        loading={loading}
        loadData={getCourses}
        page={page}
        records={courses}
        renderHeader={() => <StudentCoursesTableHeader />}
        renderRow={(course, index) => (
          <StudentCoursesTableRow
            studentEnrollmentStatus={status}
            course={course}
            key={index}
            onMarkReviewCompleted={() => {
              setCourseToMarkReview(course);
              setShowMarkReviewModal(true);
            }}
            removeCourseEnrollment={() => {
              setCourseToCancel(course);
              setShowCancelCourseEnrollmentModal(true);
            }}
            viewOnly={viewOnly}
          />
        )}
        results={results}
        showResultsCount={true}
        success={success}
        totalCount={totalCourses}
      />
      {showCancelCourseEnrollmentModal && (
        <ConfirmationModal
          cancel={() => {
            setShowCancelCourseEnrollmentModal(false);
            setCancelCourseEnrollmentErrorMessage("");
            setCancelCourseEnrollmentLoading(false);
            setCourseToCancel(null);
          }}
          confirm={handleCancelCourseEnrollment}
          errorMessage={cancelCourseEnrollmentErrorMessage}
          loading={cancelCourseEnrollmentLoading}
          message={`Are you sure you'd like to cancel ${formatFullName(
            firstName,
            lastName,
          )}'s enrollment in ${courseToCancel.courseScheduleName}?`}
          show={true}
        />
      )}
      {showMarkReviewModal && (
        <ConfirmationModal
          cancel={() => {
            setShowMarkReviewModal(false);
            setMarkReviewErrorMessage("");
            setMarkReviewLoading(false);
            setCourseToMarkReview(null);
          }}
          confirm={markReviewCompleted}
          errorMessage={markReviewErrorMessage}
          loading={markReviewLoading}
          message={`Are you sure you'd like to mark ${formatFullName(
            firstName,
            lastName,
          )}'s review for ${courseToMarkReview.courseScheduleName} as completed?${
            courseToMarkReview.didSurveyDeadlinePass
              ? "\n\nNote: The review deadline has passed. This will override the deadline."
              : ""
          }`}
          show={true}
        />
      )}
    </div>
  );
}
